import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthURL } from "../../../routes/auth_routes/AuthURL";
import ModalPopup from "../../../components/popups/DefaultPopUp";

const navItems = [
  { label: "Home", id: "hero" },
  { label: "Challenges", id: "problem" },
  { label: "Solution", id: "solution" },
  { label: "Vision", id: "vision" },
  { label: "Contact", id: "cta" },
];

const Navbar = () => {
  const brandName = process.env.REACT_APP_BRAND_NAME;
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("hero");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);

      const sections = navItems.map((item) => document.getElementById(item.id));

      let current = "hero";

      sections.forEach((section) => {
        if (!section) return;

        const top = section.offsetTop - 120;

        if (window.scrollY >= top) {
          current = section.id;
        }
      });

      setActive(current);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);

    if (!section) return;

    setMobileMenuOpen(false);

    section.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="tw-fixed tw-inset-x-0 tw-top-0 tw-z-50 tw-pt-4 tw-px-4 md:tw-px-8"
    >
      <div
        className = {
          `tw-max-w-7xl tw-mx-auto tw-transition-all tw-duration-500 tw-rounded-2xl tw-border
          ${
            scrolled
              ? // ? "tw-bg-white/10 tw-backdrop-blur-2xl tw-border-white/10 tw-shadow-2xl"
                // : "tw-bg-white/5 tw-backdrop-blur-xl tw-border-white/5"
                "tw-bg-slate-900/40 tw-backdrop-blur-2xl tw-border-white/10 tw-shadow-2xl"
              : "tw-bg-slate-900/20 tw-backdrop-blur-xl tw-border-white/5"
          }`
        }
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-h-16 tw-px-6 md:tw-px-6">
          {/* LOGO */}
          <motion.div
            whileHover={{
              scale: 1.05,
            }}
            className="tw-flex tw-items-center tw-gap-2 md:tw-gap-3 tw-cursor-pointer"
            onClick={() => scrollToSection("hero")}
          >
            <motion.div
              animate={{
                boxShadow: [
                  "0 0 8px rgba(59,130,246,.2)",
                  "0 0 24px rgba(59,130,246,.7)",
                  "0 0 8px rgba(59,130,246,.2)",
                ],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
              }}
              className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-blue-500"
            />

            <span className="tw-text-white tw-font-bold tw-tracking-wide tw-text-xl">
              {brandName}
            </span>
          </motion.div>

          {/* DESKTOP NAV */}
          <nav className="tw-hidden md:tw-flex tw-gap-10">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                // className="tw-relative tw-text-sm tw-text-gray-300 hover:tw-text-white tw-transition tw-cursor-pointer"
                className="
                  tw-relative
                  tw-bg-transparent
                  tw-border-0
                  tw-outline-none
                  tw-appearance-none
                  tw-text-sm
                  tw-text-gray-300
                  hover:tw-text-white
                  tw-transition
                  tw-cursor-pointer
                "
              >
                {item.label}

                <AnimatePresence>
                  {active === item.id && (
                    <motion.span
                      layoutId="navIndicator"
                      className="tw-absolute tw-left-0 tw--bottom-2 tw-h-[2px] tw-w-full tw-bg-blue-400 tw-rounded-full"
                    />
                  )}
                </AnimatePresence>
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="tw-hidden md:tw-flex tw-items-center tw-gap-3">
            {/* Book Demo */}
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: "0 0 25px rgba(59,130,246,.5)",
              }}
              whileTap={{ scale: 0.96 }}
              onClick={() => setShowDemoModal(true)}
              className="
                tw-px-6
                tw-py-2.5
                tw-rounded-xl
                tw-bg-gradient-to-r
                tw-from-blue-600
                tw-to-cyan-500
                tw-text-white
                tw-font-semibold
                tw-shadow-lg
              "
            >
              Book Demo
            </motion.button>

            {/* Sign In */}
            <motion.button
              whileHover={{
                scale: 1.05,
              }}
              whileTap={{ scale: 0.96 }}
              onClick={() => { navigate(AuthURL.LOGIN); }}
              className="
                tw-px-6
                tw-py-2.5
                tw-rounded-xl
                tw-bg-white/5
                tw-backdrop-blur-xl
                tw-border
                tw-border-white/10
                tw-text-white
                tw-font-medium
                hover:tw-bg-white/10
                tw-transition-all
              "
            >
              Sign In
            </motion.button>
          </div>

          {/* MOBILE MENU */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="
              md:tw-hidden
              tw-flex
              tw-items-center
              tw-justify-center
              tw-w-10
              tw-h-10
              tw-rounded-xl
              tw-bg-transparent
              tw-text-white
              tw-text-3xl
              tw-border
              tw-border-white/10
              tw-transition
              hover:tw-bg-white/5
            "
          >
            {mobileMenuOpen ? "✕" : "☰"}
          </motion.button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
              className="
                md:tw-hidden
                tw-mt-3
                tw-rounded-2xl
                tw-bg-slate-900/70
                tw-backdrop-blur-2xl
                tw-border
                tw-border-white/10
                tw-overflow-hidden
              "
            >
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`
                    tw-w-full
                    tw-text-left
                    tw-px-6
                    tw-py-4
                    tw-transition
                    hover:tw-bg-white/5
                    ${
                      active === item.id
                        ? "tw-text-cyan-400"
                        : "tw-text-white"
                    }
                  `}
                >
                  {item.label}
                </button>
              ))}

              <div className="tw-p-5 tw-border-t tw-border-white/10 tw-space-y-3">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setShowDemoModal(true);
                  }}
                  className="
                    tw-w-full
                    tw-rounded-xl
                    tw-py-3.5
                    tw-font-semibold
                    tw-text-white
                    tw-bg-gradient-to-r
                    tw-from-blue-600
                    tw-to-cyan-500
                  "
                >
                  Book Demo
                </button>

                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate(AuthURL.LOGIN);
                  }}
                  className="
                    tw-w-full
                    tw-rounded-xl
                    tw-py-3.5
                    tw-font-medium
                    tw-text-white
                    tw-bg-white/5
                    tw-backdrop-blur-xl
                    tw-border
                    tw-border-white/10
                    hover:tw-bg-white/10
                    tw-transition-all
                  "
                >
                  Sign In
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* BACKGROUND GLOW */}
      <motion.div
        animate={{
          opacity: [0.15, 0.4, 0.15],
          scale: [1, 1.15, 1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
        }}
        className="tw-absolute tw--z-10 tw-left-1/2 tw--translate-x-1/2 tw--top-6 tw-w-72 tw-h-24 tw-bg-blue-500/20 tw-blur-3xl tw-rounded-full"
      />

      <ModalPopup
        isOpen  = { showDemoModal }
        onClose = { () => setShowDemoModal(false) }
        title   = { `Book a ${brandName} Demo` }
        subtext = { `Ready to boost your firm's output? Call us at +91 97709 69714 to schedule your live ${brandName} demo and see how automation transforms your workflow.`}
        cancelText = "Maybe Later"
        actionText = "Connect Instantly"
        onAction   = { () => {
          window.location.href = "tel:+1234567890"; // Opens the dialler
          setShowDemoModal(false);                  // Closes the popup 
        }}
      />
    </motion.header>
  );
};

export default Navbar;
